import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  Font
} from '@react-pdf/renderer';
import { DisaggregatedMetric } from '../services/types';
import { formatSelectedAreaForPdf, formatValue } from '../utils/helpers';
import { categorizeData, getMetricGroupOrderIds, getPdfIcon, getSortedMetrics } from '../utils/dashboardUtils';
import React from 'react';
import PDFSimpleBarChart from '../components/pdf/PDFSimpleBarChart';
import PDFAverageChart from '../components/pdf/PDFAverageChart';
import familyProgramLogo from '../assets/family-programs-logo.png';
import caseyFirstFooterPng from '../assets/casey-programs-footer.png';
import caseyFooterPng from '../assets/casey-pdf-footer.png';
import finalFooterImg from '../assets/last-footer-casey.png';
import PDFUnemploymentRateChart from '../components/pdf/PDFUnemploymentRateChart';
import PDFEthnicityChart from '../components/pdf/PDFEthnicityChart';

Font.registerHyphenationCallback((word) => [word]);
// Shared PDF Components
// -----------------------------------------------------------------------------
const styles = StyleSheet.create({
	page: { padding: 24, fontFamily: 'Helvetica', color: '#5E4F4A', position: 'relative' },
	mapHeader: {
		backgroundColor: '#9D1E27',
		padding: 16,
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'center',
	},
	mapHeaderTitle: { fontSize: 20, color: '#E8E8E8', marginRight: 32 },
	mapHeaderSub: { fontSize: 10, color: '#FFFFFF', opacity: 0.8, paddingLeft: 32, borderLeft: 1, borderLeftColor: '#EEEEEE' },
	mapImage: { width: '100%', marginTop: 16 },
	mapFooterLabel: { color: '#FCA311', fontSize: 12, fontWeight: 700, position: 'absolute', bottom: 40, left: 15 },
	mapFooterArea: { color: '#FFFFFF', fontSize: 12, fontWeight: 700, position: 'absolute', bottom: 20, left: 15 },

	sectionHeader: {
		marginHorizontal: 8,
		marginVertical: 8,
		borderRadius: 4,
		padding: 8,
		alignItems: 'center',
		backgroundColor: '#EF8200',
	},
	sectionHeaderEducation: { backgroundColor: '#9D1E27' },
	sectionHeaderText: { fontSize: 14, color: '#FFFFFF', fontWeight: 700 },

	grid: {
		marginTop: 8,
		flexDirection: 'column',
		alignItems: 'stretch',
	},

	gridAlt: {
		marginTop: 8,
		marginLeft: 12,
		flexDirection: 'row',
		flexWrap: 'wrap',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
	},

	threeColRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		marginBottom: 8,
	},

	twoColRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		marginBottom: 8,
	},

	twoColRowChart: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		marginBottom: 8,
		marginLeft: 12
	},

	threeColItem: {
        width: '31%',
    },

	fourColItem: {
		width: '22%',
	},

	metricCardThird: {
		width: '31%',
		marginBottom: 16,
	},

	halfWidthItem: {
		width: '48%',
	},

	metricRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
	},

	metricIcon: { width: 40, height: 40, marginBottom: 4 },

	metricCard: {
	marginBottom: 16,
	borderRadius: 8,
	backgroundColor: '#FFFFFF',
	overflow: 'hidden',
	},

	metricCardContent: {
	padding: 12,
	},

	metricCardTopRow: {
	flexDirection: 'row',
	justifyContent: 'space-between',
	alignItems: 'flex-start',
	marginBottom: 6,
	},

	metricCardLeft: {
	flexDirection: 'row',
	alignItems: 'center',
	flexGrow: 1,
	flexShrink: 1,
	},

	metricIconWrapper: {
	width: 32,
	height: 32,
	borderRadius: 8,
	justifyContent: 'center',
	alignItems: 'center',
	marginRight: 8,
	padding: 4,
	},

	metricValue: {
	fontSize: 22,
	fontWeight: 700,
	lineHeight: 1,
	},

	metricTextBlock: {
	marginTop: 2,
	},

	metricTitle: {
	fontSize: 10,
	fontWeight: 700,
	color: '#3A2E29',
	},

	metricDescriptor: {
	fontSize: 8,
	color: '#6B5E58',
	marginTop: 2,
	},

	metricYear: {
	fontSize: 9,
	marginTop: 4,
	color: '#3A2E29',
	},

	metricBar: {
	height: 6,
	width: '100%',
	},

	footerImage: {
		position: 'absolute',
		bottom: 0,
		left: 0,
		width: '100%',
		height: 100,
	},

	firstFooterImage: {
		position: 'absolute',
		bottom: 0,
		left: 0,
		width: '100%',
		height: 120,
	},

	logo: {
    width: 140,
    marginBottom: 24,
  },
  paragraph: {
    marginBottom: 10,
    fontSize: 10,
  },
  link: {
    textDecoration: 'underline',
    color: '#0000EE',
  },
  spacer: {
    height: 100,
  },
  footerContainer: {
    marginTop: 32,
	flexDirection: 'row',
	justifyContent: 'space-between',
  },
  footerBackground: {
    position: 'absolute',
	bottom: 0,
	left: 0,
	width: '100%',
	height: 250,
  },
  footerContent: {
	backgroundColor: "#62504c",
    // paddingTop: 32,
	marginTop: 3,
	width: '100%',
	padding: 12,
  },
  mockBanner: {
	backgroundColor: "#a81c24",
	height: 3,
	marginTop: 7,
	width: '100%',
  },
  footerTextBlock: {
	color: '#FFF',
    fontSize: 10,
	width: '50%',
	position: 'absolute', 
	bottom: 120, 
	left: 15
  },
  contactLabel: {
    color: '#F47C00',
    fontWeight: 'bold',
    fontSize: 10,
	position: 'absolute', 
	bottom: 30, 
	left: 15
  },
  contactValue: {
	color: '#FFF',
    fontSize: 10,
	position: 'absolute', 
	bottom: 50, 
	left: 15
  },
  phoneValue: {
	color: '#FFF',
    fontSize: 10,
	position: 'absolute', 
	bottom: 30, 
	left: 25
  },
  email: {
	color: '#FFF',
    fontSize: 10,
    fontWeight: 'bold',
    position: 'absolute', 
	bottom: 10, 
	left: 15
  },
});

	/** Header with optional colored banner */
const HeaderSection: React.FC<{ selectedArea?: string; color?: string; title?: string; titleOnly?: boolean; }> = ({
	selectedArea,
	color,
	title,
	titleOnly
	}) => (
	<View>
		{!titleOnly &&
			<View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
				<Text style={{ fontSize: 12, fontWeight: 700 }}>Community Opportunity Map</Text>
				<Text style={{ fontSize: 12, fontWeight: 700, color: '#9D1E27' }}>{formatSelectedAreaForPdf(selectedArea)}</Text>
			</View>
		}
		
		{!titleOnly && 
			<View style={{ borderTopWidth: 1, borderTopColor: '#5E4F4A', marginTop: 4 }} />
		}

		{title && color && (
		<View style={[styles.sectionHeader, { backgroundColor: color }]}>
			<Text style={styles.sectionHeaderText}>{title}</Text>
		</View>
		)}
	</View>
);

/** Metric card used on all pages */
const MetricCard: React.FC<{
  metric: DisaggregatedMetric;
  color?: string;
  style?: any;
}> = ({ metric, color = '#EF8200', style }) => {
  const { name, versions, id } = metric;
  const { value, unit, year, unit_descriptor } = versions[0];

  return (
    <View style={[styles.metricCard, style]}>
      <View style={styles.metricCardContent}>
        <View style={styles.metricCardTopRow}>
          <View style={styles.metricCardLeft}>

            <Text style={{ ...styles.metricValue, color }}>
              {formatValue(value, unit, id)}
            </Text>
          </View>
        </View>

        <View style={styles.metricTextBlock}>
          <Text style={styles.metricTitle}>{name}</Text>

          {unit_descriptor ? (
            <Text style={styles.metricDescriptor}>{unit_descriptor}</Text>
          ) : null}

          <Text style={styles.metricYear}>in {year}</Text>
        </View>
      </View>
    </View>
  );
};

// PDF Document
// -----------------------------------------------------------------------------
interface MetricsPDFProps {
    pdfImageBlob?: Blob;
    logoUrl: string;
    legendUrl?: string;
    selectedArea: string;
    disaggregatedData: DisaggregatedMetric[];
	unemploymentRateChart?: UnemploymentChartData;
}

interface ChartPoint { label: string; value: number; }
interface UnemploymentChartData {
    data: ChartPoint[];
    stateData: ChartPoint[];
    nationalData: ChartPoint[];
}

const MetricsPDF: React.FC<MetricsPDFProps> = ({
	pdfImageBlob,
	logoUrl,
	legendUrl,
	selectedArea,
	disaggregatedData,
	unemploymentRateChart
	}) => {
	const categorizedData = categorizeData(disaggregatedData);
	//// Sort metrics for manual ordering
	//
	const housingOrders = getMetricGroupOrderIds("Housing");
	const demoOrders = getMetricGroupOrderIds("Demographic");

	const sortedChildMetrics = getSortedMetrics("Child & Family", categorizedData);

	// Child and Family
	const child = sortedChildMetrics[0];
	const singleParents = disaggregatedData.find((i: any) => i.id === 1106);
	const singleMothers = disaggregatedData.find((i: any) => i.id === 1108);
    const singleFathers = disaggregatedData.find((i: any) => i.id === 1109);

	// Education
	const hs = disaggregatedData.find((i: any) => i.id === 1098);
    const nohs = disaggregatedData.find((i: any) => i.id === 1104);
    const bach = disaggregatedData.find((i: any) => i.id === 1101);
	const ageSchoolEnrollment = disaggregatedData.find((i: any) => i.id === 1081);
	const fourYearGradRate = disaggregatedData.find((i: any) => i.id === 1115);

	// Economy
	const sortedEconomyMetrics = getSortedMetrics("Economy", categorizedData);
	const economy = sortedEconomyMetrics[0];
	
	// Housing
	const housingMetrics: DisaggregatedMetric[][] = housingOrders.map((order: number[]) => 
		categorizedData.Housing
			.filter(metric => order.includes(metric.id))
			.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
	);

	// Accessibility
	const healthyFood = disaggregatedData.find((i: any) => i.id === 1117);
    const snap = disaggregatedData.find((i: any) => i.id === 1110);
    const freeReducedLunch = disaggregatedData.find((i: any) => i.id === 1118);
	const internet = disaggregatedData.find((i: any) => i.id === 1111);

	const demoMetrics: DisaggregatedMetric[][] = demoOrders.map((order: number[]) => 
		categorizedData.Demographic
			.filter(metric => order.includes(metric.id))
			.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
	);


	return (
	<Document>
		{/* Page 1 – Map                                                       */}
		{/* ------------------------------------------------------------------ */}
		<Page size="LETTER" style={styles.page} wrap>
			<View style={styles.mapHeader}>
				<Text style={styles.mapHeaderTitle}>Community Opportunity Map</Text>
				<Text style={styles.mapHeaderSub}>
				Local Data on Community Health{'\n'}to Support Safe Children and{'\n'}Strong Families
				</Text>
			</View>

			{pdfImageBlob && <Image src={pdfImageBlob} style={styles.mapImage} />}

			{legendUrl || logoUrl ? (
				<View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 }}>
				{legendUrl && <Image src={legendUrl} style={{ width: 180, height: 150 }} />}
				{logoUrl && <Image src={logoUrl} style={{ width: 100, height: 40 }} />}
				</View>
			) : null}

			{/* Spacer */}
			<View style={{ height: 200 }} />

			<Image src={caseyFirstFooterPng} style={styles.firstFooterImage} />
			<Text style={styles.mapFooterLabel}>SELECTED AREA</Text>
			<Text style={styles.mapFooterArea}>{formatSelectedAreaForPdf(selectedArea)}</Text>
		</Page>

		{/* Child & Family Well-Being                                          */}
		{/* ------------------------------------------------------------------ */}
		<Page size="LETTER" style={styles.page} wrap>
			<HeaderSection
				selectedArea={selectedArea}
				color="#EF8200"
				title="Child & Family Well-Being"
			/>
			<View style={styles.grid}>
				{/* 3-column child metric cards */}
				<View style={styles.threeColRow}>
					{child.map((metric: any) => (
						<MetricCard
							key={metric.id}
							metric={metric}
							style={styles.metricCardThird}
						/>
					))}
				</View>
				<View style={styles.threeColRow}>
					{singleParents &&
						<MetricCard
							key={singleParents.id}
							metric={singleParents}
							style={styles.metricCardThird}
						/>
					}
					{singleMothers &&
						<MetricCard
							key={singleMothers.id}
							metric={singleMothers}
							style={styles.metricCardThird}
						/>
					}
					{singleFathers &&
						<MetricCard
							key={singleFathers.id}
							metric={singleFathers}
							style={styles.metricCardThird}
						/>
					}
				</View>

				{/* 2-column ratio + chart row */}
				<View style={styles.twoColRowChart}>
					<PDFSimpleBarChart
						indicators={disaggregatedData}
						metric={disaggregatedData.find(i => i.id === 1078)}
						icon={null}
						title="Children by Age Group"
						tab="Child & Family"
						chartName="childagegroup"
						barColor="#EF8200"
						reverseOrder={false}
					/>
				</View>
			</View>
			<Image src={caseyFooterPng} style={styles.footerImage} />
		</Page>

		{/* Education                                                          */}
		{/* ------------------------------------------------------------------ */}
		<Page size="LETTER" style={styles.page} wrap>
			<HeaderSection
				selectedArea={selectedArea}
				color="#9D1E27"
				title="Education"
			/>
			<View style={styles.grid}>
				<View style={styles.twoColRow}>
					{ageSchoolEnrollment &&
						<MetricCard
							key={ageSchoolEnrollment.id}
							metric={ageSchoolEnrollment}
							color='#9D1E27'
							style={styles.metricCardThird}
						/>
					}
					{fourYearGradRate &&
						<MetricCard
							key={fourYearGradRate.id}
							metric={fourYearGradRate}
							color='#9D1E27'
							style={styles.metricCardThird}
						/>
					}
				</View>
				<View style={styles.threeColRow}>
					{nohs &&
						<MetricCard
							key={nohs.id}
							metric={nohs}
							color='#9D1E27'
							style={styles.metricCardThird}
						/>
					}
					{hs &&
						<MetricCard
							key={hs.id}
							metric={hs}
							color='#9D1E27'
							style={styles.metricCardThird}
						/>
					}
					{bach &&
						<MetricCard
							key={bach.id}
							metric={bach}
							color='#9D1E27'
							style={styles.metricCardThird}
						/>
					}
				</View>
			</View>
			<Image src={caseyFooterPng} style={styles.footerImage} />
		</Page>

		{/* Economy                                                            */}
		{/* ------------------------------------------------------------------ */}
		<Page size="LETTER" style={styles.page} wrap>
			<HeaderSection selectedArea={selectedArea} color="#682145" title="Economy" />
			<View style={styles.grid}>
				<View style={styles.twoColRowChart}>
					<PDFSimpleBarChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1076)} icon={null} title="Annual Household Income" tab="Economy" chartName="annualhhinc" barColor="#682145" reverseOrder={true}/>
				</View>

				<View style={styles.threeColRow}>
					{economy.map((metric: any) => (
						<MetricCard
							key={metric.id}
							metric={metric}
							color='#682145'
							style={styles.metricCardThird}
						/>
					))}
				</View>

				{unemploymentRateChart && (
					<PDFUnemploymentRateChart
							data={unemploymentRateChart.data}
							stateData={unemploymentRateChart.stateData}
							nationalData={unemploymentRateChart.nationalData}
							lineColor="#9D1E27"
							title="Current Unemployment Rate Estimate"
							icon={null}
					/>
                )}
			</View>
			<Image src={caseyFooterPng} style={styles.footerImage} />
		</Page>

		{/* Housing and Accessibility                                                    */}
		{/* ------------------------------------------------------------------ */}
		<Page size="LETTER" style={styles.page} wrap>
			<HeaderSection selectedArea={selectedArea} color="#C1BB00" title="Housing" />
			<View style={styles.grid}>
				<View style={styles.threeColRow}>
					{housingMetrics.flat().map(metric => (
						<MetricCard key={metric.id} metric={metric} color='#C1BB00' style={styles.metricCardThird} />
					))}
				</View>
			</View>
			<HeaderSection selectedArea={selectedArea} color="#125687" title="Accessibility" titleOnly={true} />
			<View style={styles.grid}>
				<View style={styles.threeColRow}>
					{healthyFood &&
						<MetricCard
							key={healthyFood.id}
							metric={healthyFood}
							color='#125687'
							style={styles.metricCardThird}
						/>
					}
					{snap &&
						<MetricCard
							key={snap.id}
							metric={snap}
							color='#125687'
							style={styles.metricCardThird}
						/>
					}
					{freeReducedLunch &&
						<MetricCard
							key={freeReducedLunch.id}
							metric={freeReducedLunch}
							color='#125687'
							style={styles.metricCardThird}
						/>
					}
					{internet &&
						<MetricCard
							key={internet.id}
							metric={internet}
							color='#125687'
							style={styles.metricCardThird}
						/>
					}
				</View>

				<View style={styles.twoColRowChart}>
					<PDFSimpleBarChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1082)} icon={null} tab="Accessibility" chartName="commutemode" barColor="#125687" reverseOrder={false}/>
				</View>
			</View>
			<Image src={caseyFooterPng} style={styles.footerImage} />
		</Page>

		{/* Demographic                                                        */}
		{/* ------------------------------------------------------------------ */}
		<Page size="LETTER" style={styles.page} wrap>
			<HeaderSection selectedArea={selectedArea} color="#5E4F4A" title="Demographic" />
			<View style={styles.gridAlt}>
				{demoMetrics.flat().map(metric => (
				<MetricCard key={metric.id} metric={metric} color='#5E4F4A' />
				))}

				<PDFSimpleBarChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1088)} icon={getPdfIcon(1072)} title="Race" tab="Demographic" chartName="race" barColor="#808080" reverseOrder={false} sort='alphabetical'/>

				<PDFEthnicityChart indicators={disaggregatedData} icon={getPdfIcon(1072)} barColor="#808080"/>

				<PDFSimpleBarChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1113)} icon={getPdfIcon(1113)} tab="Demographic" chartName="latinodescent" barColor="#808080" reverseOrder={false}/>

				<PDFSimpleBarChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1114)} icon={getPdfIcon(1114)} tab="Demographic" chartName="language" barColor="#808080" reverseOrder={false} topFive={true}/>
			</View>
			<Image src={caseyFooterPng} style={styles.footerImage} />
		</Page>

		{/* Child & Family Well-Being Averages                                 */}
		{/* ------------------------------------------------------------------ */}
		<Page size="LETTER" style={styles.page} wrap>
			<HeaderSection
				selectedArea={selectedArea}
				color="#EF8200"
				title="Child & Family Well-Being"
			/>
			<View style={styles.gridAlt}>
				<PDFAverageChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1100)} tab="Child & Family" metricId={1100} barColor="#EF8200" reverseOrder={false} sort="alphabetical"/>

				<PDFAverageChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1089)} tab="Child & Family" metricId={1089} barColor="#EF8200" reverseOrder={false} sort="alphabetical"/>

				<PDFAverageChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1119)} tab="Child & Family" metricId={1119} barColor="#EF8200" reverseOrder={false} sort="alphabetical"/>

				<PDFAverageChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1106)} tab="Child & Family" metricId={1106} barColor="#EF8200" reverseOrder={false} sort="alphabetical"/>

				<PDFAverageChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1108)} tab="Child & Family" metricId={1108} barColor="#EF8200" reverseOrder={false} sort="alphabetical"/>

				<PDFAverageChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1109)} tab="Child & Family" metricId={1109} barColor="#EF8200" reverseOrder={false} sort="alphabetical"/>
			</View>
			<Image src={caseyFooterPng} style={styles.footerImage} />
		</Page>

		{/* Child & Family Well-Being Averages Page 2 + Economy Page 1         */}
		{/* ------------------------------------------------------------------ */}
		<Page size="LETTER" style={styles.page} wrap>
			<HeaderSection
				selectedArea={selectedArea}
				color="#EF8200"
				title="Child & Family Well-Being"
			/>
			<View style={styles.gridAlt}>
				<PDFAverageChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1102)} tab="Child & Family" metricId={1102} barColor="#EF8200" reverseOrder={false} sort="alphabetical"/>

				<PDFAverageChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1103)} tab="Child & Family" metricId={1103} barColor="#EF8200" reverseOrder={false} sort="alphabetical"/>
			</View>

			<HeaderSection selectedArea={selectedArea} color="#682145" title="Economy" titleOnly={true} />
			<View style={styles.gridAlt}>
				<PDFAverageChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1096)} tab="Economy" metricId={1096} barColor="#682145" reverseOrder={false} sort='alphabetical'/>

				<PDFSimpleBarChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1083)} tab="Economy" chartName={'hhincnative'} barColor="#682145" reverseOrder={true}/>

				<PDFSimpleBarChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1087)} tab="Economy" chartName={'hhinchawaiian'} barColor="#682145" reverseOrder={true}/>

				<PDFSimpleBarChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1070)} tab="Economy" chartName={'hhincwhite'} barColor="#682145" reverseOrder={true}/>
			</View>
			<Image src={caseyFooterPng} style={styles.footerImage} />
		</Page>

		{/* Economy Averages Page 2                                            */}
		{/* ------------------------------------------------------------------ */}
		<Page size="LETTER" style={styles.page} wrap>
			<HeaderSection selectedArea={selectedArea} color="#682145" title="Economy" />
			<View style={styles.gridAlt}>
				<PDFSimpleBarChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1085)} tab="Economy" chartName={'hhincasian'} barColor="#682145" reverseOrder={true}/>

				<PDFSimpleBarChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1068)} tab="Economy" chartName={'hhincother'} barColor="#682145" reverseOrder={true}/>

				<PDFSimpleBarChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1086)} tab="Economy" chartName={'hhincblack'} barColor="#682145" reverseOrder={true}/>

				<PDFSimpleBarChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1075)} tab="Economy" chartName={'hhinctwo'} barColor="#682145" reverseOrder={true}/>

				<PDFSimpleBarChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1069)} tab="Economy" chartName={'hhinchispanic'} barColor="#682145" reverseOrder={true}/>
			</View>
			<Image src={caseyFooterPng} style={styles.footerImage} />
		</Page>

		{/* Education Averages                                                 */}
		{/* ------------------------------------------------------------------ */}
		<Page size="LETTER" style={styles.page} wrap>
			<HeaderSection
				selectedArea={selectedArea}
				color="#9D1E27"
				title="Education"
			/>
			<View style={styles.gridAlt}>
				<PDFAverageChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1098)} tab="Education" metricId={1098} barColor="#9D1E27" reverseOrder={false} sort="alphabetical"/>

				<PDFAverageChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1104)} tab="Education" metricId={1104} barColor="#9D1E27" reverseOrder={false} sort="alphabetical"/>

				<PDFAverageChart indicators={disaggregatedData} metric={disaggregatedData.find(i => i.id === 1101)} tab="Education" metricId={1101} barColor="#9D1E27" reverseOrder={false} sort="alphabetical"/>
			</View>
			<Image src={caseyFooterPng} style={styles.footerImage} />
		</Page>

		{/* Housing + Accessibility Averages                                   */}
		{/* ------------------------------------------------------------------ */}
		<Page size="LETTER" style={styles.page} wrap>
			{/* Full-width header (above both columns) */}
			<HeaderSection selectedArea={selectedArea} />

			{/* Two-column layout for Housing + Accessibility */}
			<View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
				{/* Housing Section (Left Column) */}
				<View style={{ width: '50%' }}>
					<HeaderSection color="#C1BB00" title="Housing" titleOnly />
				</View>

				{/* Accessibility Section (Right Column) */}
				<View style={{ width: '50%' }}>
					<HeaderSection color="#125687" title="Accessibility" titleOnly />
				</View>
			</View>

			{/* Chart Grid */}
			<View style={styles.gridAlt}>
				<PDFAverageChart
					indicators={disaggregatedData}
					metric={disaggregatedData.find(i => i.id === 1092)}
					tab="Housing"
					metricId={1092}
					barColor="#C1BB00"
					reverseOrder={false}
					sort="alphabetical"
				/>
				<PDFAverageChart
					indicators={disaggregatedData}
					metric={disaggregatedData.find(i => i.id === 1110)}
					tab="Accessibility"
					metricId={1110}
					barColor="#125687"
					reverseOrder={false}
					sort="alphabetical"
				/>
			</View>
			<Image src={caseyFooterPng} style={styles.footerImage} />
		</Page>

		{/* Final Page                                                         */}
		{/* ------------------------------------------------------------------ */}
		<Page size="LETTER" style={styles.page}>
			{/* Header Logo */}
			<Image source={familyProgramLogo} style={styles.logo} />

			{/* Spacer */}
			<View style={styles.spacer} />

			{/* Paragraph Content */}
			<Text style={styles.paragraph}>
				The Community Opportunity Map allows anyone to find child and family well-being data for any neighborhood across this nation to analyze, plan, and act alongside others to build Communities of Hope. The map has 30 indicators across five topic areas: child and family well-being, education, economy, housing, and accessibility (of food, internet, and transportation). The tool uses data from the U.S. Census Bureau's American Community Survey and several other sources, covering states, counties, Congressional districts, cities, and ZIP codes in all 50 states as well as the municipios of Puerto Rico. Two-thirds of the indicators can be disaggregated by race and ethnicity.
			</Text>

			<Text style={styles.paragraph}>
				To find out more, go to{' '}
				<Text style={styles.link}>www.casey.org/community-opportunity-map</Text>.
			</Text>

			{/* Spacer */}
			<View style={{ height: 200 }} />

			{/* Footer Section */}
			{/* <View style={styles.footerContainer}>

				<View style={{ width: '50%' }}>
					<View style={styles.mockBanner}/>
					
					<View style={styles.footerContent}>
						<Text style={styles.footerTextBlock}>
							Casey Family Programs is the nation’s largest operating foundation focused on safely reducing the need for foster care in the United States. By working together, we can create a nation where Communities of Hope provide the support and opportunities that children and families need to thrive. Founded in 1966, Casey Family Programs works in all 50 states, Washington D.C., Puerto Rico, the U.S. Virgin Islands and with tribal nations across North America to influence long-lasting improvements to the well-being of children, families and the communities where they live.
						</Text>

						<Text style={styles.contactValue}>
							2001 Eighth Ave{"\n"}
							Suite 2700{"\n"}
							Seattle, WA 98121
						</Text>

						<View style={styles.contactLine}>
							<Text style={styles.contactLabel}>P</Text>
							<Text style={styles.contactValue}>206.282.7300</Text>
						</View>

						<Text style={styles.email}>ContactUs@casey.org</Text>
					</View>
				</View>
			</View> */}
			{/* Background image */}
			<Image source={finalFooterImg} style={styles.footerBackground} />
			<Text style={styles.footerTextBlock}>
				Casey Family Programs is the nation’s largest operating foundation focused on safely reducing the need for foster care in the United States. By working together, we can create a nation where Communities of Hope provide the support and opportunities that children and families need to thrive. Founded in 1966, Casey Family Programs works in all 50 states, Washington D.C., Puerto Rico, the U.S. Virgin Islands and with tribal nations across North America to influence long-lasting improvements to the well-being of children, families and the communities where they live.
			</Text>

			<Text style={styles.contactValue}>
				2001 Eighth Ave{"\n"}
				Suite 2700{"\n"}
				Seattle, WA 98121
			</Text>

			<Text style={styles.contactLabel}>P</Text>
			<Text style={styles.phoneValue}>206.282.7300</Text>
			<Text style={styles.email}>ContactUs@casey.org</Text>
		</Page>
	</Document>
)};

export default MetricsPDF;
